import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import StepShell from '@/components/StepShell';
import OptionCard from '@/components/OptionCard';
import Ornament from '@/components/Ornament';
import { useSession } from '@/store/session';
import type { AgeBand, ClientGender, Occasion, Season } from '@/types';

const GENDERS: Array<{ id: ClientGender; label: string }> = [
  { id: 'mujer', label: 'Mujer' },
  { id: 'hombre', label: 'Hombre' },
  { id: 'no_decir', label: 'Prefiero no decir' },
];

const AGES: Array<{ id: AgeBand; label: string }> = [
  { id: '18-25', label: '18 — 25' },
  { id: '26-35', label: '26 — 35' },
  { id: '36-45', label: '36 — 45' },
  { id: '46-55', label: '46 — 55' },
  { id: '55+', label: '55 +' },
];

const OCCASIONS: Array<{ id: Occasion; label: string; desc: string }> = [
  { id: 'diario',    label: 'Diario',    desc: 'Día a día' },
  { id: 'trabajo',   label: 'Trabajo',   desc: 'Oficina y reuniones' },
  { id: 'noche',     label: 'Noche',     desc: 'Cenas y salidas' },
  { id: 'especial',  label: 'Especial',  desc: 'Ocasiones únicas' },
];

const SEASONS: Array<{ id: Season; label: string; desc: string }> = [
  { id: 'primavera', label: 'Primavera', desc: 'Templado, fresco' },
  { id: 'verano',    label: 'Verano',    desc: 'Calor y ligereza' },
  { id: 'otono',     label: 'Otoño',     desc: 'Notas cálidas' },
  { id: 'invierno',  label: 'Invierno',  desc: 'Frío y calidez' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function ClientData() {
  const navigate = useNavigate();
  const { client, setClient } = useSession();

  const occasions = client.occasions ?? [];
  const seasons = client.seasons ?? [];

  const toggleOccasion = useCallback(
    (id: Occasion) => {
      const next = occasions.includes(id)
        ? occasions.filter((o) => o !== id)
        : [...occasions, id];
      setClient({ occasions: next });
    },
    [occasions, setClient]
  );

  const toggleSeason = useCallback(
    (id: Season) => {
      const next = seasons.includes(id)
        ? seasons.filter((s) => s !== id)
        : [...seasons, id];
      setClient({ seasons: next });
    },
    [seasons, setClient]
  );

  const isValid = useMemo(() => {
    return Boolean(
      client.name && client.name.trim().length >= 2 &&
      client.email && EMAIL_RE.test(client.email) &&
      client.gender &&
      client.ageBand &&
      occasions.length > 0 &&
      seasons.length > 0 &&
      client.consentEmail
    );
  }, [client, occasions, seasons]);

  return (
    <StepShell
      step={2}
      title="Cuéntanos sobre ti"
      subtitle="Para personalizar tu experiencia necesitamos algunos datos básicos."
      onBack={() => navigate('/')}
      onNext={() => navigate('/piel')}
      nextDisabled={!isValid}
    >
      {/* Nombre + email centrados */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <label className="block">
          <span className="label-field block text-center md:text-left">Nombre</span>
          <input
            type="text"
            className="input-base text-center md:text-left"
            placeholder="Cómo te llamas"
            value={client.name ?? ''}
            onChange={(e) => setClient({ name: e.target.value })}
            autoComplete="off"
            spellCheck={false}
          />
        </label>

        <label className="block">
          <span className="label-field block text-center md:text-left">Email</span>
          <input
            type="email"
            className="input-base text-center md:text-left"
            placeholder="tu@email.com"
            value={client.email ?? ''}
            onChange={(e) => setClient({ email: e.target.value.trim() })}
            autoComplete="off"
            spellCheck={false}
            inputMode="email"
          />
        </label>
      </div>

      <Ornament />

      <Section label="Género">
        <div className="grid grid-cols-3 gap-3">
          {GENDERS.map((g) => (
            <OptionCard
              key={g.id}
              label={g.label}
              active={client.gender === g.id}
              onClick={() => setClient({ gender: g.id })}
            />
          ))}
        </div>
      </Section>

      <Section label="Edad">
        <div className="grid grid-cols-5 gap-2.5">
          {AGES.map((a) => (
            <OptionCard
              key={a.id}
              label={a.label}
              active={client.ageBand === a.id}
              onClick={() => setClient({ ageBand: a.id })}
            />
          ))}
        </div>
      </Section>

      <Section
        label="¿Para qué ocasiones la buscas?"
        hint="Puedes elegir varias"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {OCCASIONS.map((o) => (
            <OptionCard
              key={o.id}
              label={o.label}
              description={o.desc}
              active={occasions.includes(o.id)}
              onClick={() => toggleOccasion(o.id)}
            />
          ))}
        </div>
      </Section>

      <Section
        label="¿En qué época del año?"
        hint="Puedes elegir varias"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {SEASONS.map((s) => (
            <OptionCard
              key={s.id}
              label={s.label}
              description={s.desc}
              active={seasons.includes(s.id)}
              onClick={() => toggleSeason(s.id)}
            />
          ))}
        </div>
      </Section>

      <Ornament />

      {/* Consentimiento */}
      <section className="card max-w-2xl mx-auto">
        <p className="label-section text-center mb-4">Consentimiento</p>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={client.consentEmail ?? false}
            onChange={(e) => setClient({ consentEmail: e.target.checked })}
            className="mt-1 w-5 h-5 accent-elixir-gold flex-shrink-0"
          />
          <span className="text-sm text-elixir-cream/85 leading-relaxed">
            Acepto recibir el resultado del diagnóstico por email.{' '}
            <span className="text-elixir-cream/55">
              Tratamos los datos según el RGPD; sólo Elixir Perfumes accede a esta información.
            </span>
          </span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer mt-3">
          <input
            type="checkbox"
            checked={client.consentMarketing ?? false}
            onChange={(e) => setClient({ consentMarketing: e.target.checked })}
            className="mt-1 w-5 h-5 accent-elixir-gold flex-shrink-0"
          />
          <span className="text-sm text-elixir-cream/85 leading-relaxed">
            Quiero recibir novedades y ofertas exclusivas en tienda.{' '}
            <span className="text-elixir-cream/55">(opcional)</span>
          </span>
        </label>
      </section>

      {!isValid && (
        <p className="mt-6 text-xs text-elixir-cream/45 text-center tracking-widest uppercase">
          Completa todos los campos para continuar
        </p>
      )}
    </StepShell>
  );
}

function Section({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="my-9">
      <div className="text-center mb-5">
        <h3 className="label-section">{label}</h3>
        {hint && (
          <p className="serif italic text-elixir-cream/55 text-xs mt-1.5">
            {hint}
          </p>
        )}
      </div>
      {children}
    </section>
  );
}
